'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocale, useTranslations } from 'next-intl';
import { useReadContract } from 'wagmi';

import { kycManagerAbi } from '@/abi';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { cn } from '@/lib/utils/cn';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { createPersonaInquiryAction } from '@/server/actions/kyc/createPersonaInquiry.action';
import { C_KYC_APPROACHING_THRESHOLD, C_KYC_LIMIT } from '@/ts/constants/C_KYC';
import { C_KYC_MANAGER_CONTRACT } from '@/ts/constants/C_KYC_MANAGER_CONTRACT';

const PERSONA_ENVIRONMENT: 'sandbox' | 'production' =
  process.env.NEXT_PUBLIC_PERSONA_ENVIRONMENT === 'production'
    ? 'production'
    : 'sandbox';

interface I_KYCAlertBannerProps {
  className?: string;
  show?: boolean;
}

interface I_PersonaClient {
  open: () => void;
  render: () => void;
  destroy: () => void;
}

export const KYCAlertBanner = ({ className, show }: I_KYCAlertBannerProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const user = useGlobalData('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [personaInquiryId, setPersonaInquiryId] = useState<string | null>(null);
  const clientRef = useRef<I_PersonaClient | null>(null);
  /** Skip destroy in effect cleanup when we closed via onComplete/onCancel/onError (avoids double-destroy) */
  const closedByUserRef = useRef(false);

  const clearPersona = useCallback(() => {
    closedByUserRef.current = true;
    try {
      clientRef.current?.destroy();
    } catch {
      // Persona may have already torn down (e.g. on cancel); ignore
    }
    clientRef.current = null;
    setPersonaInquiryId(null);
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (!personaInquiryId) return;
    if (typeof window === 'undefined') return;
    closedByUserRef.current = false;

    let isCancelled = false;
    let mountedClient: I_PersonaClient | null = null;

    const initPersona = async () => {
      const { Client } = await import('persona');

      if (isCancelled) return;

      const client = new Client({
        inquiryId: personaInquiryId,
        environment: PERSONA_ENVIRONMENT,
        onReady: () => {
          client.open();
          setIsSubmitting(false);
        },
        onComplete: () => clearPersona(),
        onCancel: () => clearPersona(),
        onError: () => clearPersona()
      });

      mountedClient = client;
      clientRef.current = client;
      client.render();
    };

    void initPersona();

    return () => {
      isCancelled = true;
      if (!closedByUserRef.current) {
        try {
          mountedClient?.destroy();
        } catch {
          // ignore if already torn down
        }
      }
      clientRef.current = null;
    };
  }, [personaInquiryId, clearPersona]);

  const { data: isKycRestrictionsEnabled } = useReadContract({
    address: C_KYC_MANAGER_CONTRACT,
    abi: kycManagerAbi,
    functionName: 'isKycRestrictionsEnabled'
  });

  const kycEnabled = isKycRestrictionsEnabled;

  // Check if user needs KYC: spent > limit for current level
  const isMaxKycLevel = user?.currentKycLevel === 3;
  const kycLimit = C_KYC_LIMIT[user?.currentKycLevel ?? 0] ?? C_KYC_LIMIT[0];
  const totalSpent = Number(user?.totalUsdcSpent) || 0;

  const needsKYC = kycEnabled
    ? !isMaxKycLevel && totalSpent >= kycLimit
    : false;

  const approachingLimit = kycEnabled
    ? !isMaxKycLevel && totalSpent >= kycLimit * C_KYC_APPROACHING_THRESHOLD
    : false;

  // Remaining USDC before limit (only when under limit); show when remaining ≤ 5% of limit
  const remainingAmount =
    !isMaxKycLevel && kycLimit > 0 && totalSpent < kycLimit
      ? (kycLimit - totalSpent).toLocaleString(locale)
      : '0';

  // Show if explicitly requested via prop, or over limit, or within 5% of limit
  const shouldShow =
    !user?.suspended &&
    (show !== undefined ? show : needsKYC || approachingLimit);
  const hasReachedLimit = needsKYC;

  const handleCompleteKYC = async () => {
    if (isSubmitting) return;
    const level = Math.min(3, Math.max(1, (user?.currentKycLevel ?? 0) + 1)) as
      | 1
      | 2
      | 3;
    setIsSubmitting(true);
    let inquiryId: string | undefined;
    try {
      const response = await createPersonaInquiryAction({ level });
      const payload = response?.data;
      inquiryId =
        payload && typeof payload === 'object' && 'inquiryId' in payload
          ? (payload as { inquiryId?: string }).inquiryId
          : (response as { inquiryId?: string } | undefined)?.inquiryId;
      if (inquiryId) {
        setPersonaInquiryId(inquiryId);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Create Persona Inquiry error:', error);
      setIsSubmitting(false);
    } finally {
      if (!inquiryId) setIsSubmitting(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <AlertBanner
      text={t.rich(
        hasReachedLimit
          ? 'profile.userInfo.kycLimitReachedLine'
          : 'profile.userInfo.kycApproachingLimitLine',
        {
          bold: chunks => <strong>{chunks}</strong>,
          limit: kycLimit.toLocaleString(locale),
          remainingAmount,
          br: txt => (
            <>
              <br /> {txt}
            </>
          )
        }
      )}
      showButton={true}
      variant="default"
      buttonText={
        isSubmitting
          ? t('profile.userInfo.loading')
          : t('profile.userInfo.completeKYC')
      }
      onButtonClick={handleCompleteKYC}
      buttonDisabled={isSubmitting}
      buttonLoading={isSubmitting}
      className={cn(className)}
    />
  );
};
